import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const usps = [
  { icon: 'ri-user-heart-line', text: 'Geen ervaring nodig' },
  { icon: 'ri-compass-3-line', text: 'Volledige begeleiding' },
  { icon: 'ri-factory-line', text: 'Direct contact met fabrieken' },
  { icon: 'ri-price-tag-3-line', text: 'Scherpe prijzen zonder tussenpersonen' },
];

const stappen = [
  { nummer: '01', title: 'Jij deelt jouw idee', desc: 'Vertel ons wat je wilt — een schets, een foto of gewoon een omschrijving is genoeg.' },
  { nummer: '02', title: 'Wij regelen alles', desc: 'Van fabriekscontact tot samples en kwaliteitscontrole. Geen kennis nodig.' },
  { nummer: '03', title: 'Levering aan je deur', desc: 'All-in geleverd in Nederland. Jij hoeft niks te regelen.' },
];

export default function GeenBedrijf() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const scrollToContact = () => { const el = document.querySelector('#contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <section ref={ref} className="py-12 md:py-20 bg-[#F7F8FA] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-10">
        <div className="text-center mb-8 md:mb-12">
          <motion.span initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-door-open-line" />
            Toegankelijk voor iedereen
          </motion.span>
          <motion.h2 initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl md:text-5xl leading-tight mb-4">
            Geen bedrijf?
            <br />
            <span className="text-[#3B9EE8]">Geen probleem!</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 }} className="text-[#0A1628]/60 text-lg leading-relaxed max-w-2xl mx-auto">
            Bij DUC helpen we iedereen — van particulier tot ondernemer. Of je nu net begint of al wilt opschalen, wij regelen alles van A tot Z.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-stretch mb-12">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.25 }} className="relative rounded-2xl overflow-hidden bg-[#0A1628] flex items-center justify-center min-h-[340px]">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0D2147] via-[#0A1628] to-[#050E1A]" />
            <div className="absolute top-0 left-0 w-64 h-64 bg-[#3B9EE8]/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#3B9EE8]/5 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 flex flex-col items-center gap-8 py-12 px-8 w-full">
              <motion.div
                whileHover={{
                  filter: 'drop-shadow(0 0 32px rgba(59,158,232,0.85)) drop-shadow(0 0 64px rgba(59,158,232,0.45))',
                  scale: 1.05,
                }}
                transition={{ duration: 0.3 }}
                className="cursor-default"
                style={{ filter: 'drop-shadow(0 0 8px rgba(59,158,232,0.3))' }}
              >
                <img
                  src="https://static.readdy.ai/image/15bdf5788918520e40e960585d4ea965/3037d4ed8e2d758e66815cdcd69eb3cd.png"
                  alt="DUC - Direct uit China"
                  className="h-[130px] w-auto object-contain"
                />
              </motion.div>
              <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
                {usps.map((usp) => (
                  <div key={usp.text} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 border border-white/8">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-[#3B9EE8] text-sm" />
                    </div>
                    <span className="text-white/65 text-xs font-500">{usp.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 bg-[#3B9EE8]/15 border border-[#3B9EE8]/30 rounded-full px-5 py-2">
                <i className="ri-door-open-line text-[#3B9EE8] text-sm" />
                <span className="text-[#3B9EE8] text-sm font-600">Voor iedereen toegankelijk</span>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 24 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.3 }} className="bg-white rounded-2xl border border-[#0A1628]/6 p-8 flex flex-col justify-between">
            <div>
              <h3 className="font-heading font-700 text-[#0A1628] text-2xl mb-6">Zo simpel werkt het</h3>
              <div className="flex flex-col gap-6 mb-8">
                {stappen.map((stap, i) => (
                  <motion.div key={stap.nummer} initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 border border-[#3B9EE8]/25">
                        <span className="font-heading font-800 text-[#3B9EE8] text-sm">{stap.nummer}</span>
                      </div>
                      {i < stappen.length - 1 && <div className="w-0.5 h-6 bg-[#3B9EE8]/20 mx-auto mt-2" />}
                    </div>
                    <div className="pt-1">
                      <h4 className="font-700 text-[#0A1628] text-base mb-1">{stap.title}</h4>
                      <p className="text-[#0A1628]/55 text-sm leading-relaxed">{stap.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mb-8">
                {usps.map((usp) => (
                  <span key={usp.text} className="inline-flex items-center gap-1.5 bg-[#F7F8FA] border border-[#0A1628]/8 rounded-full px-3 py-1.5 text-xs font-600 text-[#0A1628]/70">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`${usp.icon} text-[#3B9EE8]`} />
                    </div>
                    {usp.text}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-[#0A1628] rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#3B9EE8]/8 rounded-full blur-2xl pointer-events-none" />
              <div className="relative z-10">
                <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Gratis &amp; vrijblijvend</p>
                <button onClick={scrollToContact} className="w-full bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-base py-4 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 group mb-2">
                  <i className="ri-mail-send-line" />
                  Vraag gratis offerte aan
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
                </button>
                <p className="text-center text-white/40 text-xs">Ontvang snel een vrijblijvende offerte</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.6 }} className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: 'ri-user-heart-line', value: 'Particulier', label: 'tot ondernemer' },
            { icon: 'ri-hand-heart-line', value: '100%', label: 'Begeleiding' },
            { icon: 'ri-timer-line', value: '24u', label: 'Reactietijd' },
            { icon: 'ri-shield-check-line', value: 'Gratis', label: 'Offerte aanvragen' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-[#0A1628]/6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#3B9EE8]/10 flex-shrink-0">
                <i className={`${item.icon} text-[#3B9EE8] text-lg`} />
              </div>
              <div>
                <div className="font-heading font-800 text-[#0A1628] text-sm">{item.value}</div>
                <div className="text-[#0A1628]/45 text-xs">{item.label}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
